import React, { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const ContactPage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.contact);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d45j63vgaed3g6l52110', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          country: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(8, 145, 178, 0.8)), url('https://public.readdy.ai/ai/img_res/9585f6224247ccd97f394719311cc27a.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Contact <strong>Henatos Limited</strong>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Get in touch with Africa's trusted partner in hides and skins trade
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Head Office */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Head Office</h3>
              <div className="space-y-2 text-gray-600">
                <p>86 Colledge Road, Oke Alafia</p>
                <p>Oke Agbo, Ijebu Igbo</p>
                <p>Ogun State, Nigeria</p>
              </div>
            </div>
            
            {/* Email */}
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-cyan-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Email</h3>
              <div className="space-y-2">
                <a 
                  href="mailto:accounts@henatosbiz.com" 
                  className="text-blue-600 hover:text-blue-800 transition-colors text-lg"
                >
                  accounts@henatosbiz.com
                </a>
                <p className="text-gray-600">Business inquiries and partnerships</p>
              </div>
            </div>
            
            {/* Regional Operations */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-global-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Regional Operations</h3>
              <div className="space-y-1 text-gray-600">
                <p>Tanzania</p>
                <p>Burundi</p>
                <p>Rwanda</p>
                <p>Zambia</p>
                <p>Zimbabwe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Send Us a <strong>Message</strong>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're interested in becoming a supplier, need a quote as a buyer, 
                or have general inquiries, we're here to help.
              </p>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select your country</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Burundi">Burundi</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Zambia">Zambia</option>
                    <option value="Zimbabwe">Zimbabwe</option>
                    <option value="Ghana">Ghana</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your inquiry, requirements, or questions..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    Thank you for your message! We will get back to you within 24 hours.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    There was an error sending your message. Please try again or contact us directly.
                  </div>
                )}
                
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
            
            {/* Map */}
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Find Us</h3>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.952912260219!2d3.93!3d6.9167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwNTUnMDAuMCJOIDPCsDU1JzQ4LjAiRQ!5e0!3m2!1sen!2sng!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Henatos Limited Location"
                ></iframe>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">WhatsApp</h4>
                    <a 
                      href="https://wa.me/message" 
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Send us a message
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Response Time</h4>
                    <p className="text-gray-600">Within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Quick <strong>Actions</strong>
            </h2>
            <p className="text-xl text-gray-600">
              Choose the option that best fits your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-add-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Become a Supplier</h3>
              <p className="text-gray-600 mb-6">
                Join our network of trusted suppliers across Africa with advance deposits and steady offtake.
              </p>
              <Button href="/suppliers" variant="primary">
                Apply Now
              </Button>
            </div>
            
            <div className="bg-cyan-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shopping-cart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Buyer Inquiry</h3>
              <p className="text-gray-600 mb-6">
                Get a customized quote for consistent supply of export-grade hides to Nigeria.
              </p>
              <Button href="/buyers" variant="secondary">
                Get Quote
              </Button>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-information-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Learn More</h3>
              <p className="text-gray-600 mb-6">
                Discover our products, quality standards, and pan-African operations network.
              </p>
              <Button href="/about" variant="outline">
                About Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
